import type { UpdateFlowSchedulePreviewApiArg } from 'lib-shared-types-api';
import moment from 'moment-timezone';

import {
  getNextMonthWithYear,
  getPreviousMonthWithYear,
} from 'components/settings/components/sections/openingHours/views/schedule/utils/date';

type Date = {
  day: number;
  month: number;
  year: number;
};

type MonthAndYear = {
  month: number;
  year: number;
};

export const getCurrentDate = (): Date => {
  const momentObject = moment();

  return {
    day: momentObject.date(),
    month: momentObject.month(),
    year: momentObject.year(),
  };
};

// We compare the result of `Date` instead of individual values.
//
// It's possible we pass an invalid month because we just do
// deduction / addition to the month when doing prev / next month
// without validating if the passed month is a valid month
//
// e.g Date would know that month=-1 means go back a year or month=12 means go to next year.
export const getIsDayActive = (currentDate: Date, activeDate: Date): boolean =>
  moment.utc(currentDate).valueOf() === moment.utc(activeDate).valueOf();

export const getLastDayOfMonth = ({ year, month }: MonthAndYear): number =>
  moment({ year, month }).daysInMonth();

export const getFirstDayOfWeek = ({ year, month }: MonthAndYear): number => {
  const day = moment({ year, month, date: 1 }).isoWeekday();

  // If the day is Sunday,
  // we want it to be the last day, not first.
  //
  // Otherwise, take 1 off from the day,
  // so that first day would be Monday.
  return day === 0 ? 6 : day - 1;
};

export const getStartAndEndDate = (
  year: number,
  month: number,
  daysFromPreviousMonth: number,
  daysFromNextMonth: number,
): Pick<
  UpdateFlowSchedulePreviewApiArg['payload'],
  'start_date' | 'end_date'
> => {
  const daysInPreviousMonth = getLastDayOfMonth(
    getPreviousMonthWithYear({ year, month }),
  );
  const daysInActiveMonth = getLastDayOfMonth({ year, month });

  return {
    start_date: (daysFromPreviousMonth
      ? moment.utc({
          ...getPreviousMonthWithYear({ year, month }),
          date: daysInPreviousMonth - (daysFromPreviousMonth - 1),
        })
      : moment.utc({ year, month, date: 1 })
    ).toISOString(),
    end_date: (daysFromNextMonth
      ? moment.utc({
          ...getNextMonthWithYear({ year, month }),
          date: daysFromNextMonth,
        })
      : moment.utc({ year, month, date: daysInActiveMonth })
    ).toISOString(),
  };
};
